'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useCallback } from 'react'
import slugify from 'slugify'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'

import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
dayjs.extend(isoWeek)

import { Vendor } from '@cruz-com/lookup'
import { vendorApi } from '@/api'
import { PhoneIcon, MapIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { formatPhone, lead } from '@/lib'
import { pages } from '@/settings'
import { useGlobalStore } from '@/store/provider'

type Props = {
  id: string | any
  initialData?: Vendor.Location | any
  showLinkToLocation?: boolean
  showMessageDealer?: boolean
  showRequestHistoryReport?: boolean
}

export function DealerLocationSide({ id, initialData, showLinkToLocation, showMessageDealer, showRequestHistoryReport }: Props) {
  const { setLeadType } = useGlobalStore((state) => state)

  const navStyle = twMerge('nav-item', 'h-11')

  const { data }: { data: Vendor.Location } = useQuery({
    queryKey: [`Location_${id}`, id],
    queryFn: () => vendorApi.getLocation(id as string),
    staleTime: 3000,
    initialData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false
  })

  const { name, address1, city, state_abbr, zip_code, distance, phone, place, vehicle_count } = data || {}

  // dealer
  const dealerHref = useCallback(() => {
    return pages.dealerVehicles.href
      .replace('[dealer]', slugify(name || ''))
      .replace('[location]', slugify(`${city} ${state_abbr} ${zip_code}`))
      .replace('[id]', id)
  }, [id, name])

  if (_.isEmpty(data)) return <></>

  const NavItem = ({ children }: { children: React.ReactNode }) => {
    return (
      <div className='flex items-center w-full gap-x-3 py-0.5'>
        {children}
      </div>
    )
  }

  // address
  const Address = () => {
    if (place?.full_address) {
      const x = place.full_address.split(', ')
      const address = _.dropRight(x, 2).join(', ')
      const city_state = _.takeRight(x, 2).join(', ')
      return <>
        {address}<br />{city_state} {distance && `(${distance.toLocaleString()} mi. away)`}
      </>
    }
    return (
      <>
        {address1}<br />
        {city}, {state_abbr} {zip_code} {distance && `(${distance.toLocaleString()} mi. away)`}
      </>
    )
  }

  // overview
  const Overview = () => {
    if (!place?.overview) return <></>
    return (
      <div className='text-sm/6 mt-4'>
        {place.overview}
      </div>
    )
  }

  // rating
  // const Rating = () => {
  //   const rating = place?.rating || 0
  //   if (rating <= 3) return <></>
  //   return (
  //     <div className='flex items-center gap-x-2 text-sm'>
  //       <span>Reviews:</span>
  //       <span className='font-medium'>{rating}</span>
  //       <RatingStars rating={rating} />
  //       <span>({place?.rating_count})</span>
  //     </div>
  //   )
  // }

  // hours
  // const Hours = () => {
  //   if (_.isEmpty(place?.hours)) return <></>
  //   const wdIdx = dayjs().isoWeekday() - 1 // Monday is 1, Sunday is 7
  //   const Hour = (d: string, idx: number) => {
  //     const x = d.split(': ')
  //     return (
  //       <li key={`hour_${idx}`} className={twMerge('px-4 py-2 text-sm flex items-center justify-between', wdIdx === idx && 'font-medium')}>
  //         <span>{x[0]}</span>
  //         <span>{x[1]}</span>
  //       </li>
  //     )
  //   }
  //   const TodayHours = () => {
  //     const x = _.split(place?.hours[wdIdx], ': ')
  //     return (
  //       <button
  //         type='button'
  //         onClick={() => setShowAllHours(true)}
  //         className='w-full px-4 py-2.5 text-sm flex items-center justify-between border rounded font-medium mt-1'
  //       >
  //         <div className='text-left'>
  //           <span className='font-light text-xs'>Today's Sales Hours</span>
  //           <div>{_.last(x)}</div>
  //         </div>
  //         <ChevronDownIcon className='size-4' />
  //       </button>
  //     )
  //   }
  //   if (!showAllHours) {
  //     return <TodayHours />
  //   }
  //   return (
  //     <ul className='border rounded divide-y mt-1'>
  //       <li>
  //         <button onClick={() => setShowAllHours(false)}
  //           className='w-full px-4 py-3 flex items-center justify-between font-normal rounded-t bg-zinc-50 dark:bg-transparent'>
  //           <span className='font-medium text-sm'>Sales Hours</span>
  //           <ChevronUpIcon className='size-4' />
  //         </button>
  //       </li>
  //       {
  //         showAllHours &&
  //         _.map(place?.hours, (item, idx) => (
  //           Hour(item, idx)
  //         ))
  //       }
  //     </ul>
  //   )
  // }

  // lead
  const MessageDealer = () => {
    if (!showMessageDealer) {
      return <></>
    }

    const leadTypeList = showRequestHistoryReport
      ? lead.leadList
      : _.filter(lead.leadList, x => x.id !== lead.LeadTypeEnum.RequestHistoryReport)

    return (
      <div className='mt-8'>
        <h2 className='!text-lg/7 !pt-0'>
          Contact Seller
          <div className='border-b-3 border-red-800 w-5' />
        </h2>
        <ul role='list' className='divide-y dark:divide-zinc-700 border rounded'>
          {
            _.map(leadTypeList, ({ id, name }) =>
              <li key={`lead_${id}`}>
                <button type='button' onClick={() => setLeadType(id)} className={navStyle}>
                  {name}
                </button>
              </li>
            )
          }
        </ul>
      </div >
    )
  }

  return (
    <div className='relative max-w-[260px]'>
      <div className='text-xl/7 font-bold dark:font-medium'>
        {/* {place?.display_name || name} */}
        {name}
      </div>
      <div className='border-b-3 border-red-800 w-5' />
      <address className='not-italic text-sm/6 mt-2'>
        <Address />
      </address>
      <ul role='list' className='divide-y dark:divide-zinc-700 border rounded mt-5'>
        {
          phone &&
          <li>
            <a href={`tel:${phone}`} className={navStyle}>
              <NavItem>
                <PhoneIcon className='size-4' />
                Call {formatPhone(phone)}
              </NavItem>
            </a>
          </li>
        }
        {
          place?.google_map_uri &&
          <li>
            <a href={place.google_map_uri} target='_blank' className={navStyle}>
              <NavItem>
                <MapIcon className='size-4' />
                Google Maps
              </NavItem>
            </a>
          </li>
        }
        {
          showLinkToLocation &&
          <li>
            <Link hrefLang='en' href={dealerHref()} className={navStyle}>
              <NavItem>
                <div className='flex items-center gap-x-3'>
                  <MagnifyingGlassIcon className='size-4' />
                  Dealer's Inventory
                </div>
                <div className='nav-count'>{(vehicle_count || 0).toLocaleString()}</div>
              </NavItem>
            </Link>
          </li>
        }
      </ul>
      <Overview />
      <MessageDealer />
    </div >
  )
}